import React from "react";
import { Check } from "lucide-react";

import type { Step } from "./steps";

const ACCENT = "#f5c518";
const ACCENT_SOFT = "#fdf0c0";
const INK = "#2b0c1a";

function stepCircleStyle(
  isActive: boolean,
  isDone: boolean,
): React.CSSProperties {
  if (isActive) {
    return {
      backgroundColor: ACCENT,
      color: INK,
      boxShadow: `0 0 0 4px ${ACCENT_SOFT}`,
    };
  }
  if (isDone) return { backgroundColor: ACCENT_SOFT, color: INK };
  return {
    backgroundColor: "#fff",
    color: "#6b7280",
    border: "2px solid #e5e7eb",
  };
}

interface WizardTabsProps {
  steps: Step[];
  currentStep: number;
  onStepClick: (step: number) => void;

  allowForwardNavigation?: boolean;
}

export const WizardTabs: React.FC<WizardTabsProps> = ({
  steps,
  currentStep,
  onStepClick,
  allowForwardNavigation = false,
}) => {
  const canGo = (step: number) => {
    if (step === currentStep) return false;
    if (step < currentStep) return true;
    return allowForwardNavigation;
  };

  return (
    <div className="mb-6 flex w-full min-w-0 items-center overflow-x-auto pb-1">
      {steps.map((step, index) => {
        const isActive = step.number === currentStep;
        const isDone = step.number < currentStep;
        const clickable = canGo(step.number);

        return (
          <React.Fragment key={step.number}>
            {index > 0 && (
              <span
                aria-hidden="true"
                className="mx-1.5 h-0.5 min-w-2 flex-1 rounded-full sm:mx-2 sm:min-w-4"
                style={{
                  backgroundColor:
                    step.number <= currentStep ? ACCENT : "#e5e7eb",
                }}
              />
            )}

            <button
              type="button"
              disabled={!clickable && !isActive}
              aria-current={isActive ? "step" : undefined}
              aria-label={step.title}
              title={step.title}
              onClick={() => clickable && onStepClick(step.number)}
              className={[
                "flex min-w-0 items-center gap-2 rounded-full py-1 pl-1 transition",
                isActive ? "shrink pr-3" : "shrink-0 pr-1",
                clickable ? "cursor-pointer hover:bg-gray-100" : "cursor-default",
              ].join(" ")}
            >
              <span
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition"
                style={stepCircleStyle(isActive, isDone)}
              >
                {isDone ? (
                  <Check className="h-4 w-4" aria-hidden="true" />
                ) : (
                  step.number
                )}
              </span>
              {isActive && (
                <span
                  className="truncate text-sm font-semibold"
                  style={{ color: INK }}
                >
                  {step.title}
                </span>
              )}
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
};
